/**
 * Where the LEFT side of the file editor's compare view comes from.
 *
 * Two sources, deliberately kept behind one shape (`{ label, content }`) so the
 * diff view never branches on which one it got — it renders a label and some
 * text either way, and a third source later is a function here, not a change
 * there.
 *
 * Reading is the common case and is side-effect free. The ONE write is
 * `writeClipboardResult`, and it is explicit: the user presses a button to take
 * the merged left-hand text away with them. Nothing here ever writes to disk —
 * a compared local file is never modified, so a compare cannot damage the file
 * you compared against.
 */

/** Text was read, but there was nothing in it worth diffing against. */
export const EMPTY = 'compare_empty';
/** The user dismissed the file picker. Not an error — callers stay silent. */
export const CANCELLED = 'compare_cancelled';

/**
 * The clipboard, as text.
 *
 * Uses the Tauri clipboard plugin rather than `navigator.clipboard.readText()`:
 * in a webview the latter is gated on transient user activation and, on macOS
 * WKWebView, can surface an OS permission prompt mid-click. The plugin reads
 * through the Rust side, where neither applies.
 */
export async function readClipboardSource() {
  const { readText } = await import('@tauri-apps/plugin-clipboard-manager');
  const text = await readText();
  if (!String(text ?? '').trim()) throw new Error(EMPTY);
  return { label: 'clipboard', name: null, content: String(text) };
}

/**
 * A local file the user picks.
 *
 * `fs:allow-read-text-file` is scoped to $HOME, $DOWNLOAD, $DOCUMENT and
 * $DESKTOP, so a file chosen outside those (a mounted volume, /etc) is refused
 * by the scope check rather than by anything here — the caller surfaces that
 * failure as-is instead of pretending the file was empty.
 */
export async function readLocalFileSource(defaultName) {
  const [{ open }, { readTextFile }] = await Promise.all([
    import('@tauri-apps/plugin-dialog'),
    import('@tauri-apps/plugin-fs'),
  ]);

  const picked = await open({
    multiple: false,
    directory: false,
    title: defaultName ? `Compare "${defaultName}" with…` : 'Compare with…',
  });
  // The picker returns null on cancel, and (older shapes) an array or an
  // object with a `path`. Normalise before touching the filesystem.
  const path = Array.isArray(picked) ? picked[0] : picked?.path ?? picked;
  if (!path) throw new Error(CANCELLED);

  const content = await readTextFile(path);
  return { label: 'file', name: baseName(path), path, content: String(content ?? '') };
}

export function baseName(path) {
  const parts = String(path || '').split(/[\\/]/).filter(Boolean);
  return parts[parts.length - 1] || String(path || '');
}

/**
 * Put the merged left-hand text on the clipboard.
 *
 * This is what makes the reverse merge mean anything. The left side is a
 * scratch buffer — clipboard text, or a copy of a local file — and it is
 * deliberately never written back to disk, so without this the ← buttons would
 * assemble a result the user could not take anywhere.
 */
export async function writeClipboardResult(text) {
  const { writeText } = await import('@tauri-apps/plugin-clipboard-manager');
  await writeText(String(text ?? ''));
}
